import React, { useEffect, useState } from 'react';
import { Dispatch, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button, Select, SelectChangeEvent, MenuItem, Checkbox, ListItemText, ListSubheader } from '@mui/material'
import { showError, showToast } from '../constants/toasts';
import { GET_CUSTOMER } from '../types/General';
import { useLazyGetCustomerQuery } from '../services/customer';
import useAuth from '../hooks/useAuth';
import { useAddNotificationMutation } from '../services/notification';
import toast from 'react-hot-toast';

interface AddNotificationModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>
    getNotifiationApi: () => void
}

export default function AddNotification({ open, onClose, setOpen, getNotifiationApi }: AddNotificationModalProps) {

    const NOTIFICATION_USER_TYPE = {
        SELECTED_USER: 'SELECTED_USER',
        ALL_USER: 'ALL_USER',
        ADMIN: 'ADMIN',
        ALL_OWNER: 'ALL_OWNER',
    } as const;

    const NOTIFICATION_TYPE = {
        PUSH: 'PUSH',
        EMAIL: 'EMAIL',
    } as const;

    const [notificationType, setNotificationType] = useState<typeof NOTIFICATION_TYPE[keyof typeof NOTIFICATION_TYPE]>(NOTIFICATION_TYPE.PUSH);
    const [title, setTitle] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState<'USER' | 'OWNER' | ''>('');
    const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [recipientSearch, setRecipientSearch] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<number>(1);
    const [userData, setUserData] = useState<GET_CUSTOMER[]>([]);
    const [getUser] = useLazyGetCustomerQuery();
    const user = useAuth();
    const [addNotification, { isLoading: isSubmitting }] = useAddNotificationMutation();

    const resetForm = () => {
        setNotificationType(NOTIFICATION_TYPE.PUSH);
        setTitle('');
        setMessage('');
        setSelectedRole('');
        setSelectedRecipientIds([]);
        setAllSelected(false);
        setRecipientSearch("");
        setSearchTerm("");
        setPage(1);
        setUserData([]);
    };

    const getUserApi = async () => {
        if (!selectedRole) return; // wait until role is chosen
        setLoading(true);
        try {
            const role = selectedRole;
            const response = await getUser({
                from: page,
                search: searchTerm,
                role,
                size: 10,
                accountStatus: "APPROVED",
                country: user?.accessRoleId?.country || "",
            } as any).unwrap();
            setLoading(false);
            if (response.statusCode === 200) {
                setUserData(response?.data?.data);
            }
        } catch (error: any) {
            setLoading(false);
            showError(error?.data?.message || "");
        } finally {
            // console.log(error?.data?.message || "");

        }
    };

    useEffect(() => {
        getUserApi()
    }, [page, searchTerm, selectedRole])

    // Reset all fields whenever the modal is opened to avoid prefilled stale values
    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open]);

    // Reset recipients when role changes
    useEffect(() => {
        setSelectedRecipientIds([]);
        setAllSelected(false);
        setRecipientSearch("");
        setSearchTerm("");
    }, [selectedRole]);

    // Debounce recipient search typing → API search param
    useEffect(() => {
        const handle = setTimeout(() => {
            setSearchTerm(recipientSearch);
        }, 400);
        return () => clearTimeout(handle);
    }, [recipientSearch]);

    const handleRoleChange = (event: SelectChangeEvent) => {
        const value = event.target.value as 'USER' | 'OWNER' | '';
        setSelectedRole(value);
        setPage(1);
    };

    const handleRecipientsChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as unknown as string[];
        const allIds = (userData as any)?.map((u: any) => u?._id) || [];

        if (value.includes('ALL')) {
            if (!allSelected) {
                setSelectedRecipientIds(allIds);
                setAllSelected(true);
            } else {
                setSelectedRecipientIds([]);
                setAllSelected(false);
            }
            return;
        }

        setSelectedRecipientIds(value);
        setAllSelected(value.length === allIds.length && allIds.length > 0);
    };
    const handleSubmit = async () => {
        try {
            if (!title?.trim()) {
                showError('Title is required');
                return;
            }
            if (!message?.trim()) {
                showError('Message is required');
                return;
            }
            if (!selectedRole) {
                showError('Please select a role');
                return;
            }
            let receiverType: typeof NOTIFICATION_USER_TYPE[keyof typeof NOTIFICATION_USER_TYPE] = NOTIFICATION_USER_TYPE.SELECTED_USER;
            let receiverIds: string[] = selectedRecipientIds;

            if (allSelected) {
                receiverType = selectedRole === 'USER' ? NOTIFICATION_USER_TYPE.ALL_USER : NOTIFICATION_USER_TYPE.ALL_OWNER;
                receiverIds = [];
            } else if (!selectedRecipientIds.length) {
                toast.dismiss()
                showError('Select at least one recipient or choose Select All');
                return;
            }

            const payload = {
                title: title.trim(),
                message: message.trim(),
                notificationType,
                receiverType,
                receiverIds,
            };
            const response = await addNotification(payload as any).unwrap();
            if ((response as any)?.statusCode === 200) {
                showToast('Notification sent successfully');
                toast.dismiss()
                getNotifiationApi()
                resetForm();
                setOpen(false);
            }
        } catch (error: any) {
            showError(error?.data?.message || error?.message || 'Failed to send notification');
        }
    };

    return (
        <Modal
            className="modal addNotification_modal"
            id="addNotificationModal"
            aria-labelledby="addNotification-modal-title"
            aria-describedby="addNotification-modal-description"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>Add Notification</h2>
                        <div className="btn-close" onClick={() => { resetForm(); setOpen(false); }}>
                            <CloseIcon />
                        </div>
                    </div>
                    <form className="form" action="">
                        <div className="gap_p">
                            <div className="control_group w_100">
                                <TextField
                                    variant="outlined"
                                    placeholder="Notification Title"
                                    fullWidth
                                    hiddenLabel
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>
                            <div className="control_group w_50">
                                <Select
                                    fullWidth
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                    displayEmpty
                                >
                                    <MenuItem disabled value="">Select Role</MenuItem>
                                    <MenuItem value={'USER'}>User</MenuItem>
                                    <MenuItem value={'OWNER'}>Owner</MenuItem>
                                </Select>
                            </div>
                            {selectedRole ? (
                                <div className="control_group w_50">
                                    <Select
                                        fullWidth
                                        multiple
                                        value={selectedRecipientIds}
                                        onChange={handleRecipientsChange}
                                        renderValue={(selected) => {
                                            if ((selected as string[]).length === 0) return 'Select Recipients';
                                            if (allSelected) return 'All Selected';
                                            return `${(selected as string[]).length} Selected`;
                                        }}
                                        displayEmpty
                                        MenuProps={{
                                            PaperProps: { style: { maxHeight: 400 } },
                                        }}
                                    >
                                        <ListSubheader disableSticky>
                                            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                                                <TextField
                                                    fullWidth
                                                    placeholder="Search recipients..."
                                                    value={recipientSearch}
                                                    onChange={(e) => setRecipientSearch(e.target.value)}
                                                    size="small"
                                                />
                                            </div>
                                        </ListSubheader>
                                        <MenuItem value={'ALL'}>
                                            <Checkbox checked={allSelected} />
                                            <ListItemText primary="Select All" />
                                        </MenuItem>
                                        {(userData as any)?.map((u: any) => (
                                            <MenuItem key={u?._id} value={u?._id}>
                                                <Checkbox checked={selectedRecipientIds.indexOf(u?._id) > -1} />
                                                <ListItemText primary={u?.name || u?.email || u?._id} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            ) : null}
                            <div className="control_group w_50">
                                <Select
                                    fullWidth
                                    value={notificationType}
                                    onChange={(e) => setNotificationType(e.target.value as any)}
                                >
                                    <MenuItem value={NOTIFICATION_TYPE.PUSH}>Push</MenuItem>
                                    <MenuItem value={NOTIFICATION_TYPE.EMAIL}>Email</MenuItem>
                                </Select>
                            </div>
                            <div className="control_group w_100">
                                <TextField
                                    variant="outlined"
                                    placeholder="Notification Message"
                                    fullWidth
                                    hiddenLabel
                                    multiline
                                    maxRows={5}
                                    minRows={5}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="footer_btn_flex">
                            <Button className="btn btn_grey" onClick={() => { resetForm(); setOpen(false); }}>Cancel</Button>
                            <Button className="btn btn_primary" disabled={isSubmitting} onClick={handleSubmit}>{isSubmitting ? 'Submitting...' : 'Submit'}</Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal >
    )
}
